import React, { useState, useMemo, useEffect, useCallback } from "react";

import {
  Play,
  X,
  Clock,
  CheckCircle,
  FileText,
  Building2,
  Users,
  AlertTriangle,
} from "lucide-react";

import Button from "../../../Components/Button";
import CustomCheckbox from "../../../Components/CheckCircle";
import CommonModal from "../../../Components/Modal";
import { useAppDispatch, useAppSelector } from "../../../Store/Hooks";
import {
  fetchActivationReady,
  PrepActivatedBatch,
  ActivationReadyRelationship,
  ApprovedAssignment,
  bulkActivationAction,
  BulkActivationItem,
  BulkActivationPayload,
} from "../../../Store/Reducers/OpsSlice";
import { useAccountTiers } from "../../../Utils/Helper/accountTierHelpers";

import "./style.scss";

// Types
type RecordData = {
  id: string;
  executiveName: string;
  executiveEmail: string;
  accountName: string;
  batchSource: string;
};

type FlatRecord = {
  id: string;
  uploadId: string;
  uploadType: string;
  batchName: string;
  eventTime: string;
  eventDate: string;
  executiveData: any;
  accountData: any;
  relationshipType: string;
  type: "batch" | "assignment";
  itemId: string;
  sourceType: string;
  status: string;
  ib_result: any;
  __rowIndex?: number;
  customer_id: string;
  executive_id: string;
  assignment_type: string;
};

type DateGroup = {
  date: string;
  dateFormatted: string;
  executives: Array<{
    executiveKey: string;
    executiveData: any;
    records: FlatRecord[];
  }>;
};

interface ConfirmationModalContentProps {
  type: "activate" | "cancel";
  records: RecordData[];
  count: number;
  recordText: string;
  onClose: () => void;
  onConfirm: (
    recordsToProcess: FlatRecord[],
    options?: { is_email?: boolean }
  ) => void;
  loading: boolean;
  selectedFlatRecords?: FlatRecord[];
  allOngoingRecords?: FlatRecord[];
}

// Constants
const TABLE_HEADERS = [
  "SOURCE",
  "ACCOUNT",
  "EVENT TIME",
  "PROGRESS (ACCOUNT / BRIEFING)",
  "STATUS",
  "SPONSOR TYPE",
  "SELECT",
] as const;

// Helper Functions
const formatDateHeader = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatEventTime = (
  dateString: string
): { date: string; time: string; full: string } => {
  if (!dateString) return { date: "-", time: "-", full: "-" };

  try {
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    const tzOffset = date.getTimezoneOffset();
    const tzHours = Math.floor(Math.abs(tzOffset) / 60);
    const tzMinutes = Math.abs(tzOffset) % 60;
    const tzSign = tzOffset <= 0 ? "+" : "-";
    const tzString = `GMT${tzSign}${tzHours}:${tzMinutes.toString().padStart(2, "0")}`;

    return {
      date: formattedDate,
      time: `${formattedTime} ${tzString}`,
      full: `${formattedDate} at ${formattedTime} ${tzString}`,
    };
  } catch {
    return { date: dateString, time: "", full: dateString };
  }
};

const checkProgressStatus = (ibResults: any) => {
  if (!ibResults) {
    return {
      accountCompleted: false,
      briefingCompleted: false,
      isReady: false,
    };
  }

  if (
    typeof ibResults.is_ibaccount_created === "boolean" ||
    typeof ibResults.is_ibbriefing_generated === "boolean"
  ) {
    const accountCompleted = Boolean(ibResults.is_ibaccount_created);
    const briefingCompleted = Boolean(ibResults.is_ibbriefing_generated);

    return {
      accountCompleted,
      briefingCompleted,
      isReady: accountCompleted && briefingCompleted,
    };
  }

  const accountCompleted =
    ibResults?.accounts_created?.some(
      (account: any) =>
        account.status === "completed" ||
        account.result?.success ||
        account.result?.account
    ) || false;

  const briefingCompleted =
    ibResults?.briefings_generated?.some(
      (briefing: any) =>
        briefing.status === "completed" ||
        (briefing.result?.success && briefing.result?.briefing?.success)
    ) || false;

  return {
    accountCompleted,
    briefingCompleted,
    isReady: accountCompleted && briefingCompleted,
  };
};

const getSponsorTypeLabel = (type?: string | null) => {
  if (!type) return "Sponsor";
  const t = type.toLowerCase();
  if (t === "primary") return "Primary Sponsor";
  if (t === "secondary") return "Secondary Sponsor";
  return `${type.charAt(0).toUpperCase()}${type.slice(1)} Sponsor`;
};

const createFlatRecord = (
  rel: ActivationReadyRelationship,
  batch: PrepActivatedBatch | null,
  type: "batch" | "assignment"
): FlatRecord => {
  const eventTime =
    rel.prep_activated_at || batch?.prep_activated_at || rel.created_at;
  const eventDate = eventTime
    ? new Date(eventTime).toDateString()
    : new Date().toDateString();

  return {
    id: batch
      ? `${batch.batch_id}-${rel.id || Math.random()}`
      : `individual-${rel.id || Math.random()}`,
    uploadId: batch?.batch_id || "individual-assignments",
    uploadType: batch?.upload_type || "Assignments",
    batchName: batch?.batch_name || "Individual Approved Assignments",
    eventTime: eventTime || "",
    eventDate,
    executiveData: {
      ...rel.primary_executive,
      name: rel.primary_executive?.full_name,
      email: rel.primary_executive?.email,
      title: rel.primary_executive?.position,
    },
    accountData: {
      ...rel.account,
      industry: (rel.account as any).industry || "",
      tier: (rel.account as any).tier || "",
    },
    relationshipType: rel.relationship_type || "primary",
    type,
    itemId: batch?.batch_id || rel.id || "",
    sourceType:
      batch?.upload_type === "composite"
        ? "Composite"
        : type === "assignment"
          ? "Composite"
          : "In-App",
    status: rel.status || "pending",
    ib_result: (rel as any).ib_result || null,
    customer_id: rel.customer_id || rel.account?.customer_id || "",
    executive_id:
      rel.executiveId ||
      rel.primary_executive_id ||
      rel.primary_executive?.user_id ||
      "",
    assignment_type: rel.relationship_type || "primary",
  };
};

/** Map new API approved_assignment (one row per account+executive) to FlatRecord */
const createFlatRecordFromApprovedAssignment = (
  assignment: ApprovedAssignment
): FlatRecord => {
  const eventTime = assignment.updated_at || assignment.created_at;
  const eventDate = eventTime
    ? new Date(eventTime).toDateString()
    : new Date().toDateString();

  return {
    id: `assignment-${assignment.id}-${assignment.executive_id}`,
    uploadId: "individual-assignments",
    uploadType: "Assignments",
    batchName: assignment.batch_name || "Individual Approved Assignments",
    eventTime: eventTime || "",
    eventDate,
    executiveData: {
      ...assignment.executive,
      name: assignment.executive.full_name,
      email: assignment.executive.email,
      title: assignment.executive.position,
    },
    accountData: {
      ...assignment.account,
      industry: assignment.account.industry || "",
      tier: assignment.account.tier || "",
    },
    relationshipType: assignment.executive.type || "primary",
    type: "assignment",
    itemId: assignment.id,
    sourceType:
      assignment.assignment_source === "composite" ? "Composite" : "In-App",
    status: assignment.status || "pending",
    ib_result: assignment.ib_result ?? null,
    customer_id:
      assignment.customer_id || assignment.account?.customer_id || "",
    executive_id:
      assignment.executive_id || assignment.executive?.user_id || "",
    assignment_type: assignment.executive.type || "primary",
  };
};

// Components
const ProgressIndicator: React.FC<{ completed: boolean; label: string }> = ({
  completed,
  label,
}) => (
  <div
    className={`flex items-center gap-1.5 ${completed ? "text-green-600" : "text-gray-500"}`}
  >
    {completed ? (
      <CheckCircle className="w-4 h-4" />
    ) : (
      <Clock className="w-4 h-4" />
    )}
    <span className="text-xs">{label}</span>
  </div>
);

const StatusBadge: React.FC<{ isReady: boolean }> = ({ isReady }) => (
  <div
    className={`flex items-center gap-1.5 ${isReady ? "text-green-600" : "text-gray-500"}`}
  >
    {isReady ? (
      <>
        <CheckCircle className="w-4 h-4" />
        <span className="text-sm font-medium">Ready</span>
      </>
    ) : (
      <>
        <Clock className="w-4 h-4" />
        <span className="text-sm font-medium">Pending</span>
      </>
    )}
  </div>
);

const ConfirmationModalContent: React.FC<ConfirmationModalContentProps> = ({
  type,
  records,
  count,
  recordText,
  onClose,
  onConfirm,
  loading,
  selectedFlatRecords = [],
  allOngoingRecords = [],
}) => {
  const isActivate = type === "activate";
  const IconComponent = isActivate ? Play : X;
  const iconBgColor = isActivate ? "bg-blue-100" : "bg-red-100";
  const iconColor = isActivate ? "text-blue-600" : "text-red-600";
  const title = isActivate ? "Confirm Activation" : "Activation Removal";

  // Find companion records (same account, different sponsor type)
  const companionRecords = useMemo(() => {
    if (!isActivate || selectedFlatRecords.length === 0) return [];

    const companions: FlatRecord[] = [];
    const selectedKeys = new Set(
      selectedFlatRecords.map(r => `${r.customer_id}-${r.assignment_type}`)
    );

    selectedFlatRecords.forEach(selectedRecord => {
      const companionType =
        selectedRecord.assignment_type === "primary" ? "secondary" : "primary";

      const companion = allOngoingRecords.find(
        r =>
          r.customer_id === selectedRecord.customer_id &&
          r.assignment_type === companionType &&
          !selectedKeys.has(`${r.customer_id}-${r.assignment_type}`)
      );

      if (companion) {
        companions.push(companion);
      }
    });

    return companions;
  }, [isActivate, selectedFlatRecords, allOngoingRecords]);

  // Merge selected records with companion records
  const allRecordsToShow = useMemo(() => {
    return [...selectedFlatRecords, ...companionRecords];
  }, [selectedFlatRecords, companionRecords]);

  // State to track which records are checked in the modal
  const [checkedRecords, setCheckedRecords] = useState<Set<string>>(() => {
    // Initially check all records (both selected and companions)
    return new Set(
      allRecordsToShow.map(r => `${r.customer_id}-${r.assignment_type}`)
    );
  });

  // Update checked records when allRecordsToShow changes
  useEffect(() => {
    setCheckedRecords(
      new Set(
        allRecordsToShow.map(r => `${r.customer_id}-${r.assignment_type}`)
      )
    );
  }, [allRecordsToShow]);

  // Convert to RecordData format for display
  const displayRecords: (RecordData & {
    isCompanion: boolean;
    flatRecord: FlatRecord;
  })[] = useMemo(() => {
    const mapped = allRecordsToShow.map(record => ({
      id: `${record.customer_id}-${record.assignment_type}`,
      executiveName: record.executiveData?.name || "N/A",
      executiveEmail: record.executiveData?.email || "N/A",
      accountName: record.accountData?.name || "N/A",
      batchSource: record.sourceType || "N/A",
      isCompanion: companionRecords.some(
        c =>
          c.customer_id === record.customer_id &&
          c.assignment_type === record.assignment_type
      ),
      flatRecord: record,
    }));

    return mapped;
  }, [allRecordsToShow, companionRecords, type, selectedFlatRecords]);

  const toggleRecordCheck = (recordKey: string) => {
    setCheckedRecords(prev => {
      const newSet = new Set(prev);
      if (newSet.has(recordKey)) {
        newSet.delete(recordKey);
      } else {
        newSet.add(recordKey);
      }
      return newSet;
    });
  };

  const checkedCount = checkedRecords.size;
  const message = isActivate
    ? `You are about to activate ${checkedCount} ${checkedCount === 1 ? "record" : "records"}. This will send activation emails to the selected executives.`
    : "You have selected the following records for activation removal.";
  const confirmButtonText = isActivate
    ? `Activate ${checkedCount} ${checkedCount === 1 ? "Record" : "Records"}`
    : `Yes, Cancel ${count} ${count === 1 ? "Record" : "Records"}`;
  const confirmButtonVariant = isActivate ? "primary" : "danger";

  const handleConfirm = (isEmail = true) => {
    const recordsToProcess = allRecordsToShow.filter(r =>
      checkedRecords.has(`${r.customer_id}-${r.assignment_type}`)
    );
    onConfirm(recordsToProcess, { is_email: isEmail });
  };

  // Helper function to get prep status for a record
  const getPrepStatus = (record: FlatRecord) => {
    const { accountCompleted, briefingCompleted } = checkProgressStatus(
      record.ib_result
    );
    return {
      account: accountCompleted ? "completed" : "pending",
      briefing: briefingCompleted ? "completed" : "pending",
    };
  };

  // Check for incomplete prep (only for activate)
  const getIncompletePrepRecords = () => {
    if (!isActivate || allRecordsToShow.length === 0) {
      return [];
    }

    return allRecordsToShow
      .filter(r => checkedRecords.has(`${r.customer_id}-${r.assignment_type}`))
      .map(record => {
        const prepStatus = getPrepStatus(record);
        const isPrepComplete =
          prepStatus.account === "completed" &&
          prepStatus.briefing === "completed";

        if (!isPrepComplete) {
          return {
            record,
            prepStatus,
            accountName: record.accountData?.name || "N/A",
            executiveName: record.executiveData?.name || "N/A",
          };
        }
        return null;
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  };

  // Group companion records by account for warning message
  const companionAccountsInfo = useMemo(() => {
    if (companionRecords.length === 0) return [];

    const accountsMap = new Map<
      string,
      {
        accountName: string;
        selectedType: string;
        companionType: string;
      }
    >();

    companionRecords.forEach(companion => {
      const selected = selectedFlatRecords.find(
        s => s.customer_id === companion.customer_id
      );
      if (selected) {
        accountsMap.set(companion.customer_id, {
          accountName: companion.accountData?.name || "N/A",
          selectedType: selected.assignment_type,
          companionType: companion.assignment_type,
        });
      }
    });

    return Array.from(accountsMap.values());
  }, [companionRecords, selectedFlatRecords]);

  const incompletePrepRecords = getIncompletePrepRecords();

  return (
    <div className="p-2">
      {/* Custom Header */}
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-full ${iconBgColor} flex items-center justify-center`}
          >
            <IconComponent className={`w-5 h-5 ${iconColor}`} />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Message */}
      <p className="text-sm text-gray-700 mb-6">{message}</p>

      {/* Records Table - use table for reliable column alignment */}
      <div className="bg-white border border-gray-200 rounded-lg mb-6 overflow-hidden">
        <div className="overflow-x-auto">
          <table
            className="w-full border-collapse"
            style={{ tableLayout: "fixed" }}
          >
            <colgroup>
              {isActivate && <col style={{ width: "48px" }} />}
              <col style={{ width: isActivate ? "20%" : "24%" }} />
              <col style={{ width: isActivate ? "28%" : "32%" }} />
              <col style={{ width: isActivate ? "22%" : "26%" }} />
              <col style={{ width: isActivate ? "14%" : "18%" }} />
              <col style={{ width: "100px" }} />
            </colgroup>
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {isActivate && (
                  <th className="px-3 py-3 text-left w-12" scope="col" />
                )}
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  EXECUTIVE
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  EMAIL
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  ACCOUNT
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  SOURCE
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  TYPE
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {displayRecords.map(record => {
                const recordKey = `${record.flatRecord.customer_id}-${record.flatRecord.assignment_type}`;
                const isChecked = checkedRecords.has(recordKey);
                const rowBg = record.isCompanion ? "bg-amber-50" : "bg-white";

                return (
                  <tr
                    key={record.id}
                    className={`border-b border-gray-200 ${rowBg}`}
                  >
                    {isActivate && (
                      <td className="px-3 py-3 align-middle">
                        <CustomCheckbox
                          checked={isChecked}
                          onChange={() => toggleRecordCheck(recordKey)}
                        />
                      </td>
                    )}
                    <td className="px-3 py-3 align-middle">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <Users className="w-4 h-4 text-blue-600" />
                        </div>
                        <span className="text-sm text-gray-900 truncate">
                          {record.executiveName}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-3 align-middle min-w-0">
                      <span className="text-sm text-gray-700 truncate block">
                        {record.executiveEmail}
                      </span>
                    </td>
                    <td className="px-3 py-3 align-middle min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-4 h-4 text-green-600" />
                        </div>
                        <span className="text-sm text-gray-900 truncate">
                          {record.accountName}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-3 align-middle">
                      <span className="text-sm text-gray-700">
                        {record.batchSource}
                      </span>
                    </td>
                    <td className="px-3 py-3 align-middle">
                      <span
                        className={`text-xs font-medium ${
                          record.flatRecord.assignment_type === "primary"
                            ? "text-blue-700"
                            : "text-purple-700"
                        }`}
                      >
                        {record.flatRecord.assignment_type === "primary"
                          ? "Primary"
                          : "Secondary"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Warning for companion sponsors (primary/secondary) */}
      {isActivate && companionAccountsInfo.length > 0 && (
        <div className="mt-4 p-4 bg-amber-50 border border-amber-300 rounded-lg">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-amber-600 mr-3 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900 mb-1">
                Multiple Sponsors Detected
              </p>
              <p className="text-sm text-amber-800 mt-1">
                {companionAccountsInfo.length === 1
                  ? "The following account has"
                  : "The following accounts have"}{" "}
                both primary and secondary sponsors pending activation:
              </p>
              <ul className="text-sm text-amber-800 mt-2 space-y-1">
                {companionAccountsInfo.map((info, i) => (
                  <li key={i} className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>
                      <strong>{info.accountName}</strong> - You selected the{" "}
                      <strong>{info.selectedType}</strong> sponsor and
                      we&apos;ve also included the{" "}
                      <strong>{info.companionType}</strong> sponsor.
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-amber-900 mt-3 font-medium">
                Both sponsors are selected by default. You can uncheck any
                sponsor to skip activation.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Warning for incomplete prep when activating */}
      {isActivate && incompletePrepRecords.length > 0 && (
        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mr-3 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800">
                Warning: Some accounts or sponsors may not be ready for
                activation.
              </p>
              <p className="text-sm text-yellow-600 mt-1">
                These accounts may not have briefings available immediately:
              </p>
              <ul className="text-sm text-yellow-600 mt-2 list-disc list-inside">
                {incompletePrepRecords.map((item, i) => (
                  <li key={i}>
                    <strong>{item.accountName || item.executiveName}</strong>
                    {" - "}
                    {item.prepStatus?.briefing !== "completed" &&
                      "Briefing not ready"}
                    {item.prepStatus?.account !== "completed" &&
                      item.prepStatus?.briefing !== "completed" &&
                      ", "}
                    {item.prepStatus?.account !== "completed" &&
                      "Account sync pending"}
                  </li>
                ))}
              </ul>
              <p className="text-sm text-yellow-700 mt-2 font-medium">
                You can still proceed with activation.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Information Box (only for cancel) */}
      {!isActivate && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-red-700">
            To remove this from the activation queue, navigate to the Total
            Assignments tab and edit or delete the related assignment.
          </p>
        </div>
      )}

      {/* Action Buttons (only for activate) */}
      {isActivate && (
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Go Back
          </Button>
          <Button
            variant="outline-primary"
            onClick={() => handleConfirm(false)}
            disabled={loading || checkedCount === 0}
            loading={loading}
          >
            Activate (No Email)
          </Button>
          <Button
            variant={confirmButtonVariant}
            onClick={() => handleConfirm(true)}
            disabled={loading || checkedCount === 0}
            loading={loading}
          >
            {confirmButtonText}
          </Button>
        </div>
      )}
    </div>
  );
};

// Helper function to get date threshold based on filter
const getDateThreshold = (timeFilter: string): Date | null => {
  if (timeFilter === "all") return null;

  const now = new Date();
  const threshold = new Date(now);

  switch (timeFilter) {
    case "7-days":
      threshold.setDate(now.getDate() - 7);
      break;
    case "30-days":
      threshold.setDate(now.getDate() - 30);
      break;
    case "90-days":
      threshold.setDate(now.getDate() - 90);
      break;
    default:
      return null;
  }

  return threshold;
};

interface PendingActivationsContainerProps {
  timeFilter?: string;
}

const PendingActivationsContainer: React.FC<
  PendingActivationsContainerProps
> = ({ timeFilter = "all" }) => {
  const dispatch = useAppDispatch();
  const { formatTier } = useAccountTiers();
  const { data: activationReadyData, loading } = useAppSelector(
    state => state.ops.activationReady
  );

  const { loading: bulkActionLoading } = useAppSelector(
    state => state.ops.bulkActivationAction
  );

  useEffect(() => {
    dispatch(fetchActivationReady());
  }, [dispatch]);

  // Group activations by date and executive
  const groupedActivations = useMemo((): DateGroup[] => {
    if (!activationReadyData) return [];

    const dateThreshold = getDateThreshold(timeFilter);
    const flatRecords: FlatRecord[] = [];

    // Process prep_activated_batches (batch relationships)
    (activationReadyData.prep_activated_batches || []).forEach(
      (batch: PrepActivatedBatch) => {
        batch.relationships.forEach((rel: ActivationReadyRelationship) => {
          flatRecords.push(createFlatRecord(rel, batch, "batch"));
        });
      }
    );

    // Process approved_assignments (one row per account+executive from new API)
    (activationReadyData.approved_assignments || []).forEach(
      (assignment: ApprovedAssignment) => {
        flatRecords.push(createFlatRecordFromApprovedAssignment(assignment));
      }
    );

    // Filter by time period if specified
    const filteredRecords = dateThreshold
      ? flatRecords.filter(record => {
          if (!record.eventTime) return false;
          const recordDate = new Date(record.eventTime);
          return recordDate >= dateThreshold;
        })
      : flatRecords;

    // Group by date first, then by primary_executive
    const dateGroups = new Map<string, Map<string, FlatRecord[]>>();

    filteredRecords.forEach(record => {
      const dateKey = record.eventDate;
      const executiveKey =
        record.executiveData.email || record.executiveData.name;

      if (!dateGroups.has(dateKey)) {
        dateGroups.set(dateKey, new Map());
      }

      const executiveMap = dateGroups.get(dateKey)!;
      if (!executiveMap.has(executiveKey)) {
        executiveMap.set(executiveKey, []);
      }

      executiveMap.get(executiveKey)!.push(record);
    });

    // Convert to sorted array format
    const sortedGroups: DateGroup[] = [];

    dateGroups.forEach((executiveMap, dateKey) => {
      const executives: Array<{
        executiveKey: string;
        executiveData: any;
        records: FlatRecord[];
      }> = [];

      executiveMap.forEach((records, executiveKey) => {
        // Sort records by event time within each executive group
        records.sort((a, b) => {
          const timeA = a.eventTime ? new Date(a.eventTime).getTime() : 0;
          const timeB = b.eventTime ? new Date(b.eventTime).getTime() : 0;
          return timeB - timeA; // Most recent first
        });

        executives.push({
          executiveKey,
          executiveData: records[0].executiveData,
          records,
        });
      });

      // Sort executives by first record's event time
      executives.sort((a, b) => {
        const timeA = a.records[0].eventTime
          ? new Date(a.records[0].eventTime).getTime()
          : 0;
        const timeB = b.records[0].eventTime
          ? new Date(b.records[0].eventTime).getTime()
          : 0;
        return timeB - timeA; // Most recent first
      });

      sortedGroups.push({
        date: dateKey,
        dateFormatted: formatDateHeader(dateKey),
        executives,
      });
    });

    // Sort date groups by date (most recent first)
    sortedGroups.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return dateB - dateA;
    });

    return sortedGroups;
  }, [activationReadyData, timeFilter]);

  // State
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Handlers
  const handleRowSelect = useCallback((recordId: string, rowIndex: number) => {
    const key = `${recordId}-${rowIndex}`;
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedRows(new Set());
  }, []);

  const buildBulkActionPayload = useCallback(
    (action: "activate" | "reject"): BulkActivationPayload => {
      const items: BulkActivationItem[] = [];
      const processedIds = new Set<string>();

      // Helper function to find a record by key
      const findRecordByKey = (key: string): FlatRecord | null => {
        for (const dateGroup of groupedActivations) {
          for (const executiveGroup of dateGroup.executives) {
            for (let idx = 0; idx < executiveGroup.records.length; idx++) {
              const record = executiveGroup.records[idx];
              const recordKey = `${record.id}-${idx}`;
              if (recordKey === key) {
                return record;
              }
            }
          }
        }
        return null;
      };

      selectedRows.forEach(key => {
        const foundRecord = findRecordByKey(key);

        if (foundRecord) {
          const uniqueKey = `${foundRecord.customer_id}-${foundRecord.executive_id}-${foundRecord.assignment_type}`;
          if (!processedIds.has(uniqueKey)) {
            items.push({
              customer_id: foundRecord.customer_id,
              executive_id: foundRecord.executive_id,
              assignment_type: foundRecord.assignment_type,
            });
            processedIds.add(uniqueKey);
          }
        }
      });

      return { action, items };
    },
    [selectedRows, groupedActivations]
  );

  const getSelectedRecordsData = useMemo((): RecordData[] => {
    const records: RecordData[] = [];

    if (!groupedActivations || selectedRows.size === 0) return records;

    groupedActivations.forEach(dateGroup => {
      dateGroup.executives.forEach(executiveGroup => {
        executiveGroup.records.forEach((record, idx) => {
          const key = `${record.id}-${idx}`;
          if (selectedRows.has(key)) {
            records.push({
              id: key,
              executiveName: record.executiveData?.name || "N/A",
              executiveEmail: record.executiveData?.email || "N/A",
              accountName: record.accountData?.name || "N/A",
              batchSource: record.sourceType || "N/A",
            });
          }
        });
      });
    });

    return records;
  }, [groupedActivations, selectedRows]);

  // Get selected flat records with full details
  const getSelectedFlatRecords = useMemo((): FlatRecord[] => {
    const flatRecords: FlatRecord[] = [];

    if (!groupedActivations || selectedRows.size === 0) return flatRecords;

    groupedActivations.forEach(dateGroup => {
      dateGroup.executives.forEach(executiveGroup => {
        executiveGroup.records.forEach((record, idx) => {
          const key = `${record.id}-${idx}`;
          if (selectedRows.has(key)) {
            flatRecords.push(record);
          }
        });
      });
    });

    return flatRecords;
  }, [groupedActivations, selectedRows]);

  // Get all ongoing activation rows
  const getAllOngoingRecords = useMemo((): FlatRecord[] => {
    if (!groupedActivations) return [];

    const allRecords: FlatRecord[] = [];
    groupedActivations.forEach(dateGroup => {
      dateGroup.executives.forEach(executiveGroup => {
        allRecords.push(...executiveGroup.records);
      });
    });

    return allRecords;
  }, [groupedActivations]);

  const selectedRecordsCount = getSelectedRecordsData.length;
  const recordText = selectedRecordsCount === 1 ? "record" : "records";

  const handleActivateSelected = useCallback(() => {
    if (selectedRows.size === 0) return;
    setShowActivateModal(true);
  }, [selectedRows.size]);

  const handleCancelSelected = useCallback(() => {
    if (selectedRows.size === 0) return;
    setShowCancelModal(true);
  }, [selectedRows.size]);

  const executeBulkAction = useCallback(
    async (
      action: "activate" | "reject",
      recordsToProcess?: FlatRecord[],
      options?: { is_email?: boolean }
    ) => {
      let payload: BulkActivationPayload;

      if (recordsToProcess && recordsToProcess.length > 0) {
        // Use the provided records (from modal with companion sponsors)
        const items: BulkActivationItem[] = [];
        const processedIds = new Set<string>();

        recordsToProcess.forEach(record => {
          const uniqueKey = `${record.customer_id}-${record.executive_id}-${record.assignment_type}`;
          if (!processedIds.has(uniqueKey)) {
            items.push({
              customer_id: record.customer_id,
              executive_id: record.executive_id,
              assignment_type: record.assignment_type,
            });
            processedIds.add(uniqueKey);
          }
        });

        payload = { action, items };
      } else {
        // Use the originally selected records
        payload = buildBulkActionPayload(action);
      }

      if (action === "activate" && options?.is_email !== undefined) {
        payload.is_email = options.is_email;
      }

      if (payload.items.length === 0) {
        console.error(`No valid items to ${action}`);
        return;
      }

      try {
        await dispatch(bulkActivationAction(payload)).unwrap();
        dispatch(fetchActivationReady());
        setSelectedRows(new Set());
        if (action === "activate") {
          setShowActivateModal(false);
        } else {
          setShowCancelModal(false);
        }
      } catch (error) {
        console.error(`Failed to ${action} selected items:`, error);
      }
    },
    [buildBulkActionPayload, dispatch]
  );

  const confirmActivate = useCallback(
    (recordsToProcess: FlatRecord[], options?: { is_email?: boolean }) =>
      executeBulkAction("activate", recordsToProcess, options),
    [executeBulkAction]
  );
  const confirmCancel = useCallback(
    (recordsToProcess: FlatRecord[]) =>
      executeBulkAction("reject", recordsToProcess),
    [executeBulkAction]
  );

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] py-8 mt-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
        <p className="text-gray-600 mt-2">Loading activation-ready data...</p>
      </div>
    );
  }

  // Empty state
  if (!groupedActivations || groupedActivations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] py-8 mt-4">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <FileText className="w-8 h-8 text-gray-400" />
        </div>
        <p className="text-base font-medium text-gray-600">
          No pending activations found.
        </p>
      </div>
    );
  }

  const selectedCount = selectedRows.size;

  return (
    <div className="pending-activations">
      {/* Selection Action Bar */}
      {selectedCount > 0 && (
        <div className="selection-action-bar p-3">
          <div className="selection-count">
            {selectedCount} {selectedCount === 1 ? "row" : "rows"} selected
            <div className="text-xs text-gray-600 mt-1">
              To modify{" "}
              {selectedCount === 1
                ? "this selected assignment"
                : "these selected assignments"}
              , go to the Total Assignments tab.
            </div>
          </div>
          <div className="selection-actions">
            <Button
              variant="primary"
              className="btn-activate-selected"
              onClick={handleActivateSelected}
              disabled={bulkActionLoading}
            >
              <Play size={16} style={{ marginRight: "8px" }} />
              Activate
            </Button>
            <Button
              variant="outline-primary"
              className="btn-cancel-selected !bg-white !text-red-600 !border-gray-300 hover:!bg-gray-50"
              onClick={handleCancelSelected}
              disabled={bulkActionLoading}
            >
              <X size={16} style={{ marginRight: "8px" }} />
              Remove
            </Button>
            <Button
              variant="outline-primary"
              className="btn-clear-selection bg-white border-gray-300 text-gray-700"
              onClick={handleClearSelection}
            >
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Table Container with Fixed Header */}
      {groupedActivations.length > 0 && (
        <div className="table-container-wrapper overflow-x-auto">
          <div style={{ minWidth: "fit-content" }}>
            {/* Fixed Table Header */}
            <div className="sticky top-0 z-2 bg-white shadow">
              <div style={{ width: "fit-content", minWidth: "100%" }}>
                <div className="grid grid-cols-[200px_280px_160px_300px_150px_120px_auto] gap-3 px-3 py-3 bg-gray-100 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {TABLE_HEADERS.map(header => (
                    <div
                      key={header}
                      className={`flex items-center ${header === "SELECT" ? "justify-end" : ""}`}
                    >
                      {header}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Scrollable Content */}
            <div
              className="table-content-scrollable"
              style={{ width: "fit-content", minWidth: "100%" }}
            >
              {/* Date Groups */}
              {groupedActivations.map((dateGroup, dateIndex) => {
                const allRecordsForDate = dateGroup.executives.flatMap(
                  executiveGroup =>
                    executiveGroup.records.map((record, idx) => ({
                      ...record,
                      __rowIndex: idx,
                    }))
                );
                return (
                  <div
                    key={dateGroup.date}
                    className={"mt-4"}
                    style={{ width: "fit-content", minWidth: "100%" }}
                  >
                    {/* Custom Table Structure */}
                    <div
                      className={"bg-white shadow mb-2"}
                      style={{ width: "fit-content", minWidth: "100%" }}
                    >
                      {/* Date Header - Combined with table */}
                      <div
                        className="flex items-center justify-between px-3 py-2.5 bg-gray-50 border-b border-gray-200"
                        style={{ width: "fit-content", minWidth: "100%" }}
                      >
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-gray-500" />
                          <h3 className="text-sm font-semibold text-gray-700">
                            {dateGroup.dateFormatted}
                          </h3>
                        </div>
                        <div className="text-xs text-gray-500 bg-white px-2 py-0.5 rounded-full border">
                          {allRecordsForDate.length}{" "}
                          {allRecordsForDate.length === 1 ? "item" : "items"}
                        </div>
                      </div>

                      <div
                        className=""
                        style={{ width: "fit-content", minWidth: "100%" }}
                      >
                        {/* Table Body */}
                        <div
                          className="divide-y divide-gray-200"
                          style={{ width: "fit-content", minWidth: "100%" }}
                        >
                          {dateGroup.executives.map(
                            (executiveGroup, execIndex) => {
                              const execRecords = executiveGroup.records.map(
                                (record, idx) => ({
                                  ...record,
                                  __rowIndex: idx,
                                })
                              );

                              const execRowKeys = execRecords.map(
                                rec => `${rec.id}-${rec.__rowIndex}`
                              );
                              const execAllSelected =
                                execRecords.length > 0 &&
                                execRowKeys.every(key => selectedRows.has(key));

                              const toggleSelectExec = () => {
                                setSelectedRows(prev => {
                                  const newSet = new Set(prev);
                                  if (execAllSelected) {
                                    execRowKeys.forEach(key =>
                                      newSet.delete(key)
                                    );
                                  } else {
                                    execRowKeys.forEach(key => newSet.add(key));
                                  }
                                  return newSet;
                                });
                              };

                              return (
                                <div
                                  key={`${dateGroup.date}-${execIndex}`}
                                  style={{
                                    width: "fit-content",
                                    minWidth: "100%",
                                  }}
                                >
                                  {/* Executive Header Row */}
                                  <div
                                    className="grid grid-cols-[30px_minmax(0,1fr)_auto] gap-1 px-3 py-2 border-b border-gray-100"
                                    style={{
                                      width: "fit-content",
                                      minWidth: "100%",
                                    }}
                                  >
                                    <div className="flex items-center justify-start">
                                      <CustomCheckbox
                                        checked={execAllSelected}
                                        onChange={toggleSelectExec}
                                      />
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <div className="flex-shrink-0 h-8 w-8">
                                        <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center">
                                          <Users className="h-4 w-4 text-indigo-600" />
                                        </div>
                                      </div>
                                      <div className="leading-tight min-w-0">
                                        <div className="font-semibold text-gray-900 text-sm mb-0.5">
                                          {executiveGroup.executiveData?.name}
                                        </div>
                                        <div className="text-xs text-gray-500 flex gap-1 flex-wrap">
                                          <span className="truncate">
                                            {
                                              executiveGroup.executiveData
                                                ?.email
                                            }
                                          </span>
                                          <span>•</span>
                                          <span>
                                            {
                                              executiveGroup.executiveData
                                                ?.title
                                            }
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center justify-end text-xs text-gray-600">
                                      {execRecords.length}{" "}
                                      {execRecords.length === 1
                                        ? "assignment"
                                        : "assignments"}
                                    </div>
                                  </div>

                                  {/* Activation Data Rows */}
                                  {execRecords.map(record => {
                                    const key = `${record.id}-${record.__rowIndex}`;
                                    const eventTime = formatEventTime(
                                      record.eventTime
                                    );
                                    const {
                                      accountCompleted,
                                      briefingCompleted,
                                      isReady,
                                    } = checkProgressStatus(record.ib_result);

                                    return (
                                      <div
                                        key={key}
                                        className="grid grid-cols-[200px_280px_160px_300px_150px_120px_auto] gap-3 px-3 py-2 hover:bg-gray-50/70 transition-colors border-t border-gray-100"
                                        style={{
                                          width: "fit-content",
                                          minWidth: "100%",
                                        }}
                                      >
                                        {/* SOURCE */}
                                        <div className="flex items-center">
                                          <span
                                            className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                                              record.sourceType === "Composite"
                                                ? "bg-blue-100 text-blue-800"
                                                : "bg-purple-100 text-purple-800"
                                            }`}
                                          >
                                            <FileText className="w-3 h-3 mr-1.5 flex-shrink-0" />
                                            {record.sourceType}
                                          </span>
                                        </div>

                                        {/* ACCOUNT */}
                                        <div className="flex items-center gap-2.5 min-w-0">
                                          <div className="w-8 h-8 rounded bg-green-100 flex items-center justify-center flex-shrink-0">
                                            <Building2 className="w-4 h-4 text-green-700" />
                                          </div>
                                          <div className="min-w-0 flex-1">
                                            <div className="font-semibold text-gray-900 text-sm mb-0.5 truncate">
                                              {record.accountData?.name}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                              {record.accountData?.industry ||
                                                "N/A"}{" "}
                                              •{" "}
                                              {formatTier(
                                                record.accountData?.tier
                                              ) ||
                                                record.accountData?.tier ||
                                                "N/A"}
                                            </div>
                                          </div>
                                        </div>

                                        {/* EVENT TIME */}
                                        <div className="flex flex-col justify-center">
                                          <div className="text-sm text-gray-600 leading-tight">
                                            {eventTime.date}
                                          </div>
                                          <div className="text-xs text-gray-500 mt-0.5 leading-tight">
                                            {eventTime.time}
                                          </div>
                                        </div>

                                        {/* PROGRESS */}
                                        <div className="flex items-center gap-3">
                                          <ProgressIndicator
                                            completed={accountCompleted}
                                            label="Account"
                                          />
                                          <ProgressIndicator
                                            completed={briefingCompleted}
                                            label="Briefing"
                                          />
                                        </div>

                                        {/* STATUS */}
                                        <div className="flex items-center">
                                          <StatusBadge isReady={isReady} />
                                        </div>

                                        {/* SPONSOR TYPE */}
                                        <div className="flex items-center">
                                          <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-700">
                                            {getSponsorTypeLabel(
                                              record.relationshipType ||
                                                record.executiveData?.type
                                            )}
                                          </span>
                                        </div>

                                        {/* Checkbox */}
                                        <div className="flex items-center justify-end">
                                          <CustomCheckbox
                                            checked={selectedRows.has(key)}
                                            onChange={() =>
                                              handleRowSelect(
                                                record.id,
                                                record.__rowIndex!
                                              )
                                            }
                                          />
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            }
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modals */}
      {selectedRecordsCount > 0 && (
        <>
          <CommonModal
            isOpen={showActivateModal}
            toggle={() => setShowActivateModal(false)}
            title=""
            size="4xl"
            centered
            showHeader={false}
          >
            <ConfirmationModalContent
              type="activate"
              records={getSelectedRecordsData}
              count={selectedRecordsCount}
              recordText={recordText}
              onClose={() => setShowActivateModal(false)}
              onConfirm={confirmActivate}
              loading={bulkActionLoading}
              selectedFlatRecords={getSelectedFlatRecords}
              allOngoingRecords={getAllOngoingRecords}
            />
          </CommonModal>

          <CommonModal
            isOpen={showCancelModal}
            toggle={() => setShowCancelModal(false)}
            title=""
            size="4xl"
            centered
            showHeader={false}
          >
            <ConfirmationModalContent
              type="cancel"
              records={getSelectedRecordsData}
              count={selectedRecordsCount}
              recordText={recordText}
              onClose={() => setShowCancelModal(false)}
              onConfirm={confirmCancel}
              loading={bulkActionLoading}
              selectedFlatRecords={getSelectedFlatRecords}
              allOngoingRecords={getAllOngoingRecords}
            />
          </CommonModal>
        </>
      )}
    </div>
  );
};

export default PendingActivationsContainer;
